import React, { useEffect, useRef } from 'react'
import { IonRouterOutlet, IonSplitPane } from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'
import { Route, Redirect } from 'react-router-dom'
import { StatusBar, Style } from '@capacitor/status-bar'
import { SplashScreen } from '@capacitor/splash-screen'
import PlatformService from './services/platform'

// Components
import MainTabs from './components/MainTabs'
import AuthGuard from './components/AuthGuard'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import OnboardingPage from './pages/onboarding/OnboardingPage'
import LandingPage from './pages/LandingPage'
import PrivacyPolicy from './pages/legal/PrivacyPolicy'
import TermsOfService from './pages/legal/TermsOfService'
import GDPRCompliance from './pages/legal/GDPRCompliance'

// Services
import { supabase } from './services/supabase'
import { useAuthStore } from './store/authStore'
import UserService from './services/userService'

// Contexts
import { ThemeProvider } from './contexts/ThemeContext'

const App: React.FC = () => {
  const { user, setUser, setSupabaseUser, loading, setLoading, isAdmin } = useAuthStore()
  const initializationRef = useRef<boolean>(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Detect browser for specific handling
  const isChromiumBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    return userAgent.includes('chrome') || userAgent.includes('edge') || userAgent.includes('chromium')
  }

  // Helper function to create minimal user object
  const createMinimalUser = (supabaseUser: any) => ({
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    full_name: supabaseUser.user_metadata?.full_name || '',
    role: supabaseUser.user_metadata?.role || 'coachee',
    avatar_url: '',
    // Check user metadata for onboarding status, default to false only if not set
    onboarding_completed: supabaseUser.user_metadata?.onboarding_completed === true,
    show_motivational_messages: supabaseUser.user_metadata?.show_motivational_messages !== false,
    created_at: supabaseUser.created_at,
    updated_at: supabaseUser.updated_at || supabaseUser.created_at,
  })

  // Helper function to clear timeout and set loading to false
  const clearTimeoutAndStopLoading = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setLoading(false)
  }

  useEffect(() => {
    // Prevent multiple initializations
    if (initializationRef.current) {
      return
    }
    initializationRef.current = true

    // Initialize Capacitor plugins only on native platforms
    if (PlatformService.isNative()) {
      // Hide splash screen
      SplashScreen.hide().catch(console.error)

      // Set status bar style
      StatusBar.setStyle({ style: Style.Default }).catch(console.error)
    }

    // Set a timeout for initialization
    const browserTimeout = isChromiumBrowser() ? 5000 : 7000
    timeoutRef.current = setTimeout(() => {
      console.warn(`Authentication initialization timed out after ${browserTimeout/1000} seconds (${isChromiumBrowser() ? 'Chromium' : 'Other'} browser)`)
      setLoading(false)
    }, browserTimeout)

    // Initialize auth state
    const initializeAuth = async () => {
      try {
        console.log('Initializing authentication...', { browser: isChromiumBrowser() ? 'Chromium' : 'Other' })
        
        // For Chromium browsers, try to clear any stale auth state first
        if (isChromiumBrowser()) {
          try {
            const storedSession = localStorage.getItem('sb-auth-token')
            if (storedSession) {
              const parsed = JSON.parse(storedSession)
              const expiresAt = parsed?.expires_at
              if (expiresAt && new Date(expiresAt * 1000) < new Date()) {
                console.log('Clearing expired session from localStorage')
                localStorage.removeItem('sb-auth-token')
              }
            }
          } catch (storageError) {
            console.warn('Error checking localStorage session:', storageError)
          }
        }
        
        // Get session with timeout
        const sessionTimeout = isChromiumBrowser() ? 2500 : 4000
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session fetch timeout')), sessionTimeout)
        )
        
        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as any
        
        if (error) {
          console.error('Error getting session:', error)
          clearTimeoutAndStopLoading()
          return
        }
        
        if (session?.user) {
          console.log('Session found during initialization, loading user profile...')
          setSupabaseUser(session.user)
          
          try {
            // Get or create user profile with timeout
            const profileTimeout = isChromiumBrowser() ? 1500 : 2500
            const profilePromise = UserService.getOrCreateProfile(session.user)
            const profileTimeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Profile fetch timeout')), profileTimeout)
            )
            
            const profile = await Promise.race([
              profilePromise,
              profileTimeoutPromise
            ]) as any
            
            if (profile) {
              console.log('User profile loaded during initialization:', profile.email)
              setUser(profile)
              await UserService.createInitialPreferences(session.user.id).catch(console.error)
            } else {
              console.error('Failed to load user profile during initialization, creating minimal user')
              setUser(createMinimalUser(session.user))
            }
          } catch (profileError) {
            console.error('Error loading user profile during initialization, creating minimal user:', profileError)
            setUser(createMinimalUser(session.user))
          }
          
          // Clear timeout and loading since we have a user
          clearTimeoutAndStopLoading()
        } else {
          console.log('No active session found during initialization')
          clearTimeoutAndStopLoading()
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        // Don't clear loading here - let the auth state change handler do it
      } finally {
        console.log('Auth initialization complete')
      }
    }

    // Listen for auth changes BEFORE initializing
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email || 'no user')
        
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          console.log('Auth state change: User signed in, processing...')
          
          // Immediately clear any pending timeouts and set supabase user
          clearTimeoutAndStopLoading()
          setSupabaseUser(session.user)
          
          // Set loading back to true while we fetch the profile
          console.log('Auth state change: Setting loading to true for profile fetch')
          setLoading(true)
          
          try {
            console.log('Auth state change: Starting profile fetch for user:', session.user.id)
            
            // Get or create user profile with a timeout
            const profilePromise = UserService.getOrCreateProfile(session.user)
            const profileTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Profile fetch timeout in auth state change')), 3000)
            )
            
            const profile = await Promise.race([profilePromise, profileTimeout]) as any
            
            if (profile) {
              console.log('Auth state change: User profile loaded successfully:', profile.email)
              setUser(profile)
              
              try {
                console.log('Auth state change: Creating initial preferences')
                await UserService.createInitialPreferences(session.user.id)
                console.log('Auth state change: Initial preferences created successfully')
              } catch (prefsError) {
                console.warn('Auth state change: Failed to create initial preferences:', prefsError)
              }
            } else {
              console.error('Auth state change: Failed to load user profile, creating minimal user')
              const minimalUser = createMinimalUser(session.user)
              console.log('Auth state change: Created minimal user:', minimalUser)
              setUser(minimalUser)
            }
          } catch (error) {
            console.error('Auth state change: Error loading user profile, creating minimal user:', error)
            const minimalUser = createMinimalUser(session.user)
            console.log('Auth state change: Created minimal user after error:', minimalUser)
            setUser(minimalUser)
          } finally {
            // Always set loading to false after processing
            console.log('Auth state change: Setting loading to false - processing complete')
            setLoading(false)
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('Auth state change: User signed out')
          clearTimeoutAndStopLoading()
          setUser(null)
          setSupabaseUser(null)
        } else {
          // For other events, just clear loading
          console.log('Auth state change: Other event, clearing loading:', event)
          clearTimeoutAndStopLoading()
        }
      }
    )

    // Start initialization after setting up the listener
    initializeAuth()

    return () => {
      subscription.unsubscribe()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [setUser, setSupabaseUser, setLoading])

  // Show loading screen while authentication is being initialized
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-secondary-500">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading InsightFlow...</p>
          <p className="text-sm mt-2 opacity-75">Initializing authentication...</p>
        </div>
      </div>
    )
  }

  return (
    <ThemeProvider>
      <IonReactRouter>
        <IonSplitPane contentId="main">
          <IonRouterOutlet id="main">
            <Route exact path="/landing" component={LandingPage} />
            <Route exact path="/login" component={LoginPage} />
            <Route exact path="/register" component={RegisterPage} />
            <Route exact path="/reset-password" component={ResetPasswordPage} />
            <Route exact path="/onboarding" component={OnboardingPage} />
            <Route exact path="/privacy-policy" component={PrivacyPolicy} />
            <Route exact path="/terms-of-service" component={TermsOfService} />
            <Route exact path="/gdpr-compliance" component={GDPRCompliance} />
            
            <Route path="/app">
              <AuthGuard>
                <MainTabs />
              </AuthGuard>
            </Route>
            
            <Route exact path="/">
              {user ? (
                isAdmin() ? <Redirect to="/app/admin" /> : <Redirect to="/app/dashboard" />
              ) : (
                <LandingPage />
              )}
            </Route>
          </IonRouterOutlet>
        </IonSplitPane>
      </IonReactRouter>
    </ThemeProvider>
  )
}

export default App 